/* eslint-disable no-restricted-syntax */
const trapFocusHandlers = {};
const Utility = {

  /**
    * Gathers all focusable elements and return Node Array
    *
    * @return {Node array} elements - Focusable elements
  */
  getFocusableElements(container) {
    return Array.from(
      container.querySelectorAll(
        ".filter__container, [type='button'], a[href], a[data-href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):not([tabindex^='-']):enabled, select:enabled, textarea:enabled, object, iframe"
      )
    );
  },

  /**
    * Attempts to focus an element, and if unsuccessful adds tabindex to the
    * element and focuses it. Tabindex is removed on element blur.
    *
    * @param {HTML Node} element - The element to be focused
  */
  forceFocus(element) {    
    if(!element) return;
    
    element.focus();

    let isFocused = false;
    if (element == document.activeElement) {
      isFocused = true;
    }

    if(!isFocused){
      element.setAttribute('tabindex', '0');
      setTimeout(() =>{
        element.focus();
      }, 500);
    }
  },

  /**
    * Trap focus within container between first and last element
    *
    * @param {Node Array} elements - container to trap focus
  */
  trapFocus(container, elementToFocus = container) {
    const elements = this.getFocusableElements(container);
    const first = elements[0];
    const last = elements[elements.length - 1];
    
    this.removeTrapFocus();

    trapFocusHandlers.focusin = (event) => {
      // console.log(event.target, container, last, first);
      if (
        event.target !== container &&
        event.target !== last &&
        event.target !== first
      )
      return;

      document.addEventListener('keydown', trapFocusHandlers.keydown);
    };

    trapFocusHandlers.focusout = () => {
      document.removeEventListener('keydown', trapFocusHandlers.keydown);
    };

    trapFocusHandlers.keydown = (event) => {
      if (event.code && event.code.toUpperCase() !== 'TAB') return; // If not TAB key
      if (event.target === last && !event.shiftKey) {
        event.preventDefault();
        first.focus();
      }

      //  On the first focusable element and tab backward, focus the last element.
      if (
        (event.target === container || event.target === first) &&
        event.shiftKey
      ) {
        event.preventDefault();
        last.focus();
      }
    };

    document.addEventListener('focusout', trapFocusHandlers.focusout);
    document.addEventListener('focusin', trapFocusHandlers.focusin);

    if(!elementToFocus || elementToFocus == undefined) elementToFocus = container;
    elementToFocus.focus();
  },

  /**
    * Release focus from focused elements
    *
    * @param {Node Array} elements - Release focus from these elements
  */
  removeTrapFocus(elementToFocus = null) {
    document.removeEventListener('focusin', trapFocusHandlers.focusin);
    document.removeEventListener('focusout', trapFocusHandlers.focusout);
    document.removeEventListener('keydown', trapFocusHandlers.keydown);

    if (elementToFocus) elementToFocus.focus();
  },

  /**
    * Limit the number of times a function is called within defined timeout
    *
    * @param {function} fn - function to execute
    * @param {integer} wait - Timeout to fire event
  */
  debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  },

  /**
   * Return an object from an array of objects that matches the provided key and value
   *
   * @param {array} array - Array of objects
   * @param {string} key - Key to match the value against
   * @param {string} value - Value to get match of
   */
  findInstance(array, key, value) { // Return an object from an array of objects that matches the provided key and value
    for (let i = 0; i < array.length; i += 1) {
      if (array[i][key] === value) {
        return array[i];
      }
    }
    return null;
  },

  /**
   * Remove an object from an array of objects by matching the provided key and value
   *
   * @param {array} array - Array of objects
   * @param {string} key - Key to match the value against
   * @param {string} value - Value to get match of
   */
   removeInstance(array, key, value) {
    if (!Array.isArray(array)) {
      throw new TypeError('Expected an array');
    }

    for (let i = 0; i < array.length; i+=1) {
      if (array[i][key] === value) {
        array.splice(i, 1);
        return array; // Return the modified array
      }
    }
    return array; // Return the array unchanged if no instance was found
  },

  /**
   * _.compact from lodash
   * Remove empty/false items from array
   * Source: https://github.com/lodash/lodash/blob/master/compact.js
   *
   * @param {array} array
   */
   compact(array) {
    if (!Array.isArray(array)) {
      throw new TypeError('Expected an array');
    }

    const result = [];
    for (let i = 0; i < array.length; i+=1) {
        const value = array[i];
        if (value) {
            result.push(value);
        }
    }
    return result;
  },

  /**
   * Remove duplicates from an array of objects
   * @param arr - Array of objects
   * @param prop - Property of each object to compare
   * @returns {Array}
   */
  removeDuplicates(arr, key = 'id') {
    const map = new Map();
    // eslint-disable-next-line array-callback-return
    arr.map((el) => {
      if (!map.has(el[key])) {
        map.set(el[key], el);
      }
    });
    return [...map.values()];
  },

  /**
   * Format date
   * @param  {string}
   * @return {String} value - formatted value
   */
  formatDate(value) {
    const date = new Date(value);
    const ye = new Intl.DateTimeFormat('en', { year: 'numeric' }).format(date);
    const mo = new Intl.DateTimeFormat('en', { month: 'short' }).format(date);
    const da = new Intl.DateTimeFormat('en', { day: '2-digit' }).format(date);
    return `${mo} ${da} ${ye}`;
  },

  /**
   * Converts a string as handle
   *
   * @param {*} str - String to check
   * @returns {*} - Returns the resolved string
   */
  handleize(str) {
    return str.toLowerCase().replace(/[^\w\u00C0-\u024f]+/g, '-').replace(/^-+|-+$/g, '');
  },

  /**
   * Converts a handle to readable friendly format
   *
   * @param {*} str - String to check
   * @returns {*} - Returns the resolved string
   */
  unhandleize(str) {
    const returnString = this._toCamelCase(str.replace(/-/g, ' '));
    return returnString;
  },

  /**
    * Serialize form data
    * @param {form} form - target form
  */
  serializeForm(form) {
    if (!(form instanceof HTMLFormElement)) {
        throw new TypeError('Expected a form element');
    }

    const obj = {};
    const formData = new FormData(form);
    const regex = /^properties\[(.*?)\]$/;

    for (const key of formData.keys()) {
        const match = regex.exec(key);
        if (match) {
            const propertyKey = match[1]; // Extract the property key
            obj.properties = obj.properties || {};
            obj.properties[propertyKey] = formData.get(key);
        } else {
            obj[key] = formData.get(key);
        }
    }
    return obj;
  },

  /**
    * Fetch call config
    *
    * @param {string} type - request Accept type
  */
  fetchConfig(type = 'json') {
    return {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': `application/${type}` }
    };
  },

  removeProtocol(path) {
    return path.replace(/http(s)?:/, '');
  },

  setCookie(cookieName, value, expdays) { // set cookies
    const expdate = new Date();
    expdate.setDate(expdate.getDate() + expdays);
    const expString = (expdate == null) ? "" : `; expires="${expdate.toUTCString()}; path=/`;
    const cookieValue = `${encodeURI(value)}${expString}`;
    document.cookie = `${cookieName}=${cookieValue}`;

    return document.cookie;
  },

  getCookie(cookieName) { // get cookies
    const name = `${cookieName}=`;
    const decodedCookie = decodeURIComponent(document.cookie);
    const ca = decodedCookie.split(';');
    for(const element of ca) {
        let c = element;
        while (c.charAt(0) == ' ') {
          c = c.substring(1);
        }
        if (c.indexOf(name) == 0) {
          return c.substring(name.length, c.length);
        }
    }
    return "";
  },

  removeCookie(cookieName) { // remove cookie
    document.cookie = `${cookieName}=;max-age=0`;
    return document.cookie;
  },

  /**
   * Build URL based on following parameters
   * @param {*} url 
   * @param {*} paramName 
   * @param {*} paramValue 
   */
  replaceUrlParam(url, paramName, paramValue){
    const pattern = new RegExp(`(\\?|\\&)(${paramName}=).*?(&|$)`);
    let newUrl = url;
    if(url.search(pattern) >= 0){
      newUrl = url.replace(pattern,`$1$2${paramValue}$3`);
    }else{
      newUrl = `${newUrl + (newUrl.indexOf('?')>0 ? '&' : '?')  }${paramName}=${paramValue}`;
    }

    return newUrl
  },

  /**
   * local storage handling code 
   * @param {*} key 
   * @param {*} value 
   * @returns 
   */
  setLocalStorage(key,value) {
    window.localStorage.setItem(key, value);
    return window.localStorage;
  },

  /**
   * Get Local Storage value based on key
   * @param {*} key 
   */
  getLocalStorage(key) {
    return window.localStorage.getItem(key);
  },

  /**
   * Remove Local Storage value based on key
   * @param {*} key 
   */
  removeFromLocalStorage(key) {
    window.localStorage.removeItem(key);
    return window.localStorage;
  },

  /**
   * session storage handling code
   * @param {*} key 
   * @param {*} value 
 */
  setSessionStorage(key,value) {
    window.sessionStorage.setItem(key, value);
    return window.sessionStorage;
  },

  /**
   * Get Session Storage value based on key
   * @param {*} key 
   */
  getSessionStorage(key) {
    return window.sessionStorage.getItem(key);
  },

  /**
   * Remove session storage based on key
   * @param {*} key 
   * @returns 
   */
  removeFromSessionStorage(key) {
    window.sessionStorage.removeItem(key);
    return window.sessionStorage;
  },

  /**
   * Get URL parameter
   */
  getUrlParameter(name) {
    name = name.replace(/[\[]/, '\\[').replace(/[\]]/, '\\]');
    const regex = new RegExp(`[\\?&]${name}=([^&#]*)`);
    const results = regex.exec(window.location.search);
    return results === null ? '' : decodeURIComponent(results[1].replace(/\+/g, ' '));
  },

  /**
   * Vibrate mobile device
   */
  vibrateDevice() {
    const vibrationEnabled = navigator.vibrate || navigator.webkitVibrate || navigator.mozVibrate || navigator.msVibrate;
    if (vibrationEnabled) {
      navigator.vibrate(100);
    }
  },

  /**
   * XMLHTTPS request using javascript
  */
  _XMLHttpRequest(method, URL, data, callback) {
    const newDataRequest = new XMLHttpRequest();
    newDataRequest.open(method, URL, true);
    newDataRequest.setRequestHeader('Accept', 'application/json')
    newDataRequest.setRequestHeader('Content-Type', 'application/json')
    newDataRequest.onload = () => {
        callback(newDataRequest.status, this.response)
    }
    if(method == 'GET'){ newDataRequest.send();}
    else{ newDataRequest.send(JSON.stringify(data));}
  },

  /**
   * manage history
   */
  goBack() { // Previous page
    return window.history.back()
  },

  goForward() { // Forward page
    return window.history.forward()
  },

  /**
   * Push History State
   * @param {String} state 
   * @param {String} title 
   * @param {String} url 
   */
  pushHistoryState(state,title,url) {
    return window.history.pushState(state, title, url)
  },

  /**
   * Replace History State
   * @param {String} state 
   * @param {String} title 
   * @param {String} url 
   */
  replaceHistoryState(state,url) {
    return window.history.replaceState(state, '', url)
  },

  /**
   * Get swatch hex color code or image url
   *
   * @param {string} colorName
   * @returns {string} background style hex code
  */
  getSwatchStyle(colorName) {
    const swatchesColorList = JSON.parse(document.querySelector('[data-swatches-colorlist-json]').innerText);
    colorName = colorName.replace(/-|\s/g, '').toLowerCase();
    const swatch = swatchesColorList[colorName];
    let swatchStyle;
    if (typeof swatch !== 'undefined') {
      if (swatch.match(/\.(jpeg|jpg|png|gif)/g) != null) {
        swatchStyle = `background-image: url(${swatch})`;
      } else {
        swatchStyle = `background-color: ${swatch}`;
      }
      return swatchStyle;
    }
    return false;
  },

  /**
   * Truncates a given string
   *
   * @param {String} value - Value to check
   * @param {Number} count - Count number of words
   * @returns {*} - Returns the resolved value
   */
  truncate(value, count) {
    const strippedString = value.trim();
    const array = strippedString.split(' ');
    value = array.splice(0, count).join(' ');
    if (array.length > count) {
      value += '...';
    }
    return value;
  },

  /**
   * detect if the device is touch based
   */

  is_touch_enabled() {
    return ( 'ontouchstart' in window ) || ( navigator.maxTouchPoints > 0 ) ||  ( navigator.msMaxTouchPoints > 0 );
  },

  getAllSiblings(element, parent) {
    const children = [...parent.children];
    return children.filter(child => child !== element);
  },

  /**
   * Toggle animation for element
   * @param {Node} container - Parent node of toggled element
   * @param {String} activity - open/close
   */
  toggleElement(container, activity) {
    const contentBlock = container.querySelector('[data-type="content"]') || container.querySelector('.toggle-content');
    if(!contentBlock) return;

    if(activity == 'open'){
      [contentBlock,container].forEach(ele => ele.classList.add('open'));
      contentBlock.setAttribute('aria-hidden', false);
    }else{
      [contentBlock,container].forEach(ele => ele.classList.remove('open'));
      contentBlock.setAttribute('aria-hidden', true);
    }
  },

   /**
   * Fade Effect
   */
   fadeEffect(elem, action) {
    const FADE_STEP = 0.01; // Amount to change opacity by
    const FADE_INTERVAL = 10; // Interval in milliseconds

    if (action === 'fadeIn') {
      elem.style.display = 'block';
      elem.style.opacity = 0;
      const fadeIn = () => {
          let newValue = parseFloat(elem.style.opacity);
          if (newValue < 1) {
              newValue += FADE_STEP;
              elem.style.opacity = newValue;
              requestAnimationFrame(fadeIn);
          } else {
              elem.style.opacity = 1; // Ensure opacity is set to 1
          }
      };
      requestAnimationFrame(fadeIn);
    } else if (action === 'fadeOut') {
      elem.style.opacity = 1;
      const fadeOut = () => {
          let newValue = parseFloat(elem.style.opacity);
          if (newValue > 0) {
              newValue -= FADE_STEP;
              elem.style.opacity = newValue;
              requestAnimationFrame(fadeOut);
          } else {
              elem.style.opacity = 0; // Ensure opacity is set to 0
              elem.style.display = 'none';
          }
      };
      requestAnimationFrame(fadeOut);
    }
  },

  /**
   * Email Validation
   * @param {*} email 
   */
  validateEmail(email){
    return email.match(
      /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    );
  },

  /**
   * Format money values based on your shop currency settings
   * @param  {Number|string} cents - value in cents or dollar amount e.g. 300 cents or 3.00 dollars
   * @return {String} value - formatted value
   */
	formatMoney(cents, format) {
    if (format == null || format == undefined){
      format = window.globalSpace.money_format || "${{ amount }}";
    }
    if (typeof cents == 'string') { cents = cents.replace('.',''); }
    var value = '';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var formatString = (format || this.money_format);
    function defaultOption(opt, def) {
      
      return (typeof opt == 'undefined' ? def : opt);
    }
    function formatWithDelimiters(number, precision, thousands, decimal, hideZeros) {
      precision = defaultOption(precision, 2);
      thousands = defaultOption(thousands, ',');
      decimal   = defaultOption(decimal, '.');
      if (isNaN(number) || number == null) { return 0; }
      number = (number/100.0).toFixed(precision);
      var parts   = number.split('.'),
          dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands),
          cents   = parts[1] ? (decimal + parts[1]) : '';
      if (hideZeros && cents === '.00') {
        return dollars; // Skip the decimal if it's .00
      }
      return dollars + cents;
    }
    switch(formatString.match(placeholderRegex)[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2, ',', '.', true);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
    }
    return formatString.replace(placeholderRegex, value);
  },

  syncProductInstallmentSubtotal(subtotalPriceCents) {
    if (subtotalPriceCents == null || Number.isNaN(subtotalPriceCents)) return;

    const formattedSubtotal = this.formatMoney(
      subtotalPriceCents,
      window.globalSpace?.money_format
    );

    document.querySelectorAll('[data-product-installment-subtotal]').forEach((el) => {
      el.textContent = formattedSubtotal;
      el.dispatchEvent(new Event('change', { bubbles: true }));
      el.dispatchEvent(new Event('input', { bubbles: true }));
    });

    this.patchShopifyPaymentTermsForAmount(subtotalPriceCents);

    requestAnimationFrame(() => window.syncAffirmTextFromInstallments?.());
    setTimeout(() => window.syncAffirmTextFromInstallments?.(), 400);
    setTimeout(() => window.syncAffirmTextFromInstallments?.(), 900);
  },

  parseShopPayFinancingPlans(shopifyPaymentTerms) {
    if (!shopifyPaymentTerms) return null;

    try {
      const meta = JSON.parse(shopifyPaymentTerms.getAttribute('shopify-meta') || '{}');
      return {
        minPriceCents: this.parseMoneyToCents(meta.min_price),
        maxPriceCents: this.parseMoneyToCents(meta.max_price),
        financingPlans: meta.financing_plans || [],
      };
    } catch {
      return null;
    }
  },

  parseMoneyToCents(value) {
    if (value == null || value === '') return null;
    const normalized = String(value).replace(/[^0-9.]/g, '');
    if (!normalized) return null;
    return Math.round(parseFloat(normalized) * 100);
  },

  calculateShopPayStartingMonthly(priceCents, financingPlans) {
    if (!Array.isArray(financingPlans) || !priceCents) return null;

    const price = priceCents / 100;
    const plan = financingPlans.find((entry) => {
      const min = parseFloat(String(entry.min_price).replace(/[$,]/g, ''));
      const max = parseFloat(String(entry.max_price).replace(/[$,]/g, ''));
      return price >= min && price <= max;
    });

    if (!plan || !Array.isArray(plan.terms)) return null;

    let lowestMonthly = null;

    plan.terms.forEach((term) => {
      let monthly = null;

      if (term.loan_type === 'split_pay' && term.installments_count) {
        monthly = price / term.installments_count;
      } else if (term.loan_type === 'interest' && term.installments_count) {
        const apr = Number(term.apr) || 0;
        const months = term.installments_count;
        if (apr === 0) {
          monthly = price / months;
        } else {
          const rate = apr / 100 / 12;
          const factor = Math.pow(1 + rate, months);
          monthly = price * ((rate * factor) / (factor - 1));
        }
      }

      if (monthly != null && (lowestMonthly == null || monthly < lowestMonthly)) {
        lowestMonthly = monthly;
      }
    });

    return lowestMonthly;
  },

  getShopPayMonthlyFormatted(priceCents) {
    const shopifyPaymentTerms = document.querySelector('shopify-payment-terms');
    const financingMeta = this.parseShopPayFinancingPlans(shopifyPaymentTerms);
    if (!financingMeta) return null;

    const { minPriceCents, maxPriceCents, financingPlans } = financingMeta;
    if (minPriceCents != null && priceCents < minPriceCents) return null;
    if (maxPriceCents != null && priceCents > maxPriceCents) return null;

    const monthly = this.calculateShopPayStartingMonthly(priceCents, financingPlans);
    if (monthly == null) return null;

    const formattedMonthly = this.formatMoney(Math.round(monthly * 100), window.globalSpace?.money_format);
    return `${formattedMonthly}/mo`;
  },

  updateProductInstallmentMonthlyDisplay(priceCents) {
    const formattedMonthly = this.getShopPayMonthlyFormatted(priceCents);
    if (!formattedMonthly) return;

    document.querySelectorAll('[data-product-installment-monthly]').forEach((el) => {
      el.textContent = formattedMonthly;
    });
  },

  patchShopifyPaymentTermsForAmount(priceCents, attempt = 0) {
    const shopifyPaymentTerms = document.querySelector('shopify-payment-terms');
    if (!shopifyPaymentTerms) {
      if (attempt < 10) {
        setTimeout(() => this.patchShopifyPaymentTermsForAmount(priceCents, attempt + 1), 300);
      }
      return;
    }

    this.updateProductInstallmentMonthlyDisplay(priceCents);

    const paymentTermsElements = document.querySelectorAll('[data-product-installments] shopify-payment-terms');
    const formattedMonthly = this.getShopPayMonthlyFormatted(priceCents);
    if (!formattedMonthly) return;

    const formattedAmount = formattedMonthly.replace(/\/mo$/, '');
    let patched = false;

    paymentTermsElements.forEach((paymentTermsEl) => {
      const contentEl = paymentTermsEl.shadowRoot?.getElementById('shopify-installments-content');
      const monthlyEl = contentEl?.querySelector('.font-bold');
      if (monthlyEl) {
        monthlyEl.textContent = formattedAmount;
        patched = true;
      }
    });

    if (paymentTermsElements.length && !patched && attempt < 10) {
      setTimeout(() => this.patchShopifyPaymentTermsForAmount(priceCents, attempt + 1), 300);
    }
  },

  arrayIncludes(arr, value) {
    for (let i = 0; i < arr.length; i+=1) {
      if (arr[i] == value) return true;
    }
    return false;
  },

  uniq(arr) {
    const result = [];
    for (let i = 0; i < arr.length; i+=1) {
      if (!this.arrayIncludes(result, arr[i])) {
        result.push(arr[i]);
      }
    }
    return result;
  },

  /**
   * Reads per-variant metafields from a single [data-variant-metafields-map] JSON object
   * keyed by variant id. Tries preferredRoot (e.g. closest product-form) first, then the document
   * (needed when the cart adds an accessory variant from another product card on the page).
   * @param {number|string} variantId
   * @param {ParentNode|null} preferredRoot
   * @returns {Record<string, unknown>|null}
   */
  WATER_BOWL_HANDLES: new Set(['houndsy-water-bowl', 'houndsy-water-bowl-holiday']),
  KIBBLE_SENSOR_HANDLES: new Set(['kibble-sensor', 'houndsy-kibble-sensor']),

  isKibbleSensorHandle(productHandle) {
    return Boolean(productHandle && Utility.KIBBLE_SENSOR_HANDLES.has(productHandle));
  },

  normalizeMetafieldScalar(raw) {
    if (raw == null || raw === '') return null;
    return typeof raw === 'object' && raw !== null && 'value' in raw ? raw.value : String(raw);
  },

  getProductMetafieldsFromDom(productHandle) {
    if (!productHandle) return null;
    const readFromScript = (el) => {
      if (!el?.textContent?.trim()) return null;
      try {
        const map = JSON.parse(el.textContent);
        const payload = map[productHandle];
        if (payload == null || typeof payload !== 'object') return null;
        return payload;
      } catch {
        return null;
      }
    };
    const maps = document.querySelectorAll('[data-product-metafields-map]');
    for (let i = 0; i < maps.length; i += 1) {
      const hit = readFromScript(maps[i]);
      if (hit) return hit;
    }
    return null;
  },

  /**
   * Water bowl only: product metafield custom.shipping_message → line property "Ships Separately".
   * @param {string} productHandle
   * @returns {string|null}
   */
  getShipsSeparatelyForProductHandle(productHandle) {
    if (!productHandle || !Utility.WATER_BOWL_HANDLES.has(productHandle)) return null;
    const productMf = Utility.getProductMetafieldsFromDom(productHandle);
    return Utility.normalizeMetafieldScalar(productMf?.shipping_message);
  },

  /**
   * Water bowl only: resolves shipping_message from variant map, then product map.
   * @param {number|string} variantId
   * @param {ParentNode|null} preferredRoot
   * @returns {string|null}
   */
  getShipsSeparatelyForVariant(variantId, preferredRoot = null) {
    const custom = Utility.getVariantMetafieldsCustomFromDom(variantId, preferredRoot);
    const handle = custom?.product_handle;
    if (!handle || !Utility.WATER_BOWL_HANDLES.has(handle)) return null;

    const fromVariant = Utility.normalizeMetafieldScalar(custom.shipping_message);
    if (fromVariant) return fromVariant;

    return Utility.getShipsSeparatelyForProductHandle(handle);
  },

  getVariantMetafieldsCustomFromDom(variantId, preferredRoot = null) {
    if (variantId == null || variantId === '') return null;
    const key = String(variantId);
    const readFromScript = (el) => {
      if (!el?.textContent?.trim()) return null;
      try {
        const map = JSON.parse(el.textContent);
        const payload = map[key];
        if (payload == null || typeof payload !== 'object') return null;
        return payload;
      } catch {
        return null;
      }
    };
    if (preferredRoot) {
      const scoped = preferredRoot.querySelector('[data-variant-metafields-map]');
      const hit = readFromScript(scoped);
      if (hit) return hit;
    }
    const maps = document.querySelectorAll('[data-variant-metafields-map]');
    for (let i = 0; i < maps.length; i += 1) {
      const hit = readFromScript(maps[i]);
      if (hit) return hit;
    }
    return null;
  },

  isAccessoryMetafieldDiscountEnabled(accessoriesWrap) {
    return accessoriesWrap?.getAttribute('data-enable-metafield-discount') !== 'false';
  },

  isAccessoryWaterBowlPromo(accessoriesWrap, priceEle) {
    return (
      accessoriesWrap?.hasAttribute('data-water-bowl-product') &&
      priceEle?.getAttribute('data-discount-percentage') === '100'
    );
  },

  shouldUsePlainAccessoryPrice(accessoriesWrap, priceEle) {
    return (
      !Utility.isAccessoryMetafieldDiscountEnabled(accessoriesWrap) &&
      !Utility.isAccessoryWaterBowlPromo(accessoriesWrap, priceEle)
    );
  }
}